// @oasis-permission-inherits: /ai-edit-tool (ai-edit-v2-tool:view)
import { Icon } from "@mirage-web/icons";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { PermissionBanner } from "~/components/permission-banner";
import { Button } from "~/components/ui/button";
import { ShotStyleForm, type OverlayItem } from "~/modules/ai-edit-tool/components/shot-style-form";
import { ShotStyleModeIndicator } from "~/modules/ai-edit-tool/components/shot-style-mode-indicator";
import {
  DisplayCategory,
  DisplayCategoryStringToNum,
  type DisplayCategoryValue,
  ShotType,
  type ShotTypeValue,
  type SoundEffectConfig,
} from "~/modules/ai-edit-tool/constants";
import { useStyleEditorPermissions } from "~/modules/ai-edit-tool/hooks/use-style-editor-permissions";
import { convertOverlaysToApiFormat } from "~/modules/ai-edit-tool/lib/converters";
import { hasInvalidAiTitleLayerReferences } from "~/modules/ai-edit-tool/lib/overlay-validation";
import {
  createCaptions,
  createSoundEffect,
  type CaptionLayout,
} from "~/modules/ai-edit-tool/lib/template-builder";
import {
  addShotStyleToVideoStyle,
  upsertAiEditShotStyle,
} from "~/modules/ai-edit-tool/network/mutations";
import { getAiEditShotStyles, getAiEditStyles } from "~/modules/ai-edit-tool/network/queries";
import {
  VideoType,
  type CaptionsColorOverrideConfig,
  type StrokeStyle,
} from "~/modules/ai-edit-tool/schemas";

export const Route = createFileRoute("/_dash/ai-edit-tool/new/sst/$videoStyleId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { canEdit, canEditStyle } = useStyleEditorPermissions();
  const navigate = useNavigate();
  const { videoStyleId } = Route.useParams();

  const canEditVideoStyle = canEditStyle(videoStyleId);

  const { data: videoStylesData } = getAiEditStyles.useQuery({
    params: {
      client: "DESKTOP",
      version: "dev",
      afterCursor: "",
      tags: [],
      pageSize: 150,
    },
  });
  const videoStyle = videoStylesData?.styles?.page.find((style) => style.id === videoStyleId);

  const { data: shotStylesData } = getAiEditShotStyles.useQuery(
    videoStyleId
      ? {
          params: { videoStyleId },
        }
      : skipToken
  );

  const shotStyles = useMemo(() => {
    return shotStylesData?.shotTemplates || [];
  }, [shotStylesData]);

  const hasAnyShots = shotStyles.length > 0;

  const handleNavigateToPrevious = useCallback(() => {
    if (shotStyles.length > 0) {
      const lastShotStyle = shotStyles[shotStyles.length - 1];
      navigate({
        to: "/ai-edit-tool/sst/$shotStyleId",
        params: { shotStyleId: lastShotStyle.id },
        search: { mode: "view", videoStyleId },
      });
    }
  }, [shotStyles, navigate, videoStyleId]);

  const handleNavigateToNext = useCallback(() => {
    if (shotStyles.length > 0) {
      const firstShotStyle = shotStyles[0];
      navigate({
        to: "/ai-edit-tool/sst/$shotStyleId",
        params: { shotStyleId: firstShotStyle.id },
        search: { mode: "view", videoStyleId },
      });
    }
  }, [shotStyles, navigate, videoStyleId]);

  const [displayName, setDisplayName] = useState("");
  const [shotType, setShotType] = useState<ShotTypeValue>(ShotType.SHOT_TYPE_UNSPECIFIED);
  const [displayCategory, setDisplayCategory] = useState<DisplayCategoryValue>(
    DisplayCategory.CATEGORY_UNSPECIFIED
  );
  const [supportedVideoTypes, setSupportedVideoTypes] = useState<VideoType[]>([]);
  const [isDefaultShotStyle, setIsDefaultShotStyle] = useState(false);

  const [captionLayout, setCaptionLayout] = useState<CaptionLayout>("lower");
  const [captionOverrideTemplateId, setCaptionOverrideTemplateId] = useState("");
  const [captionColorOverrides, setCaptionColorOverrides] = useState<CaptionsColorOverrideConfig[]>(
    []
  );

  const [foregroundOverlays, setForegroundOverlays] = useState<OverlayItem[]>([]);
  const [backgroundOverlays, setBackgroundOverlays] = useState<OverlayItem[]>([]);

  const [soundEffectConfigs, setSoundEffectConfigs] = useState<SoundEffectConfig[]>([]);
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  const [strokeStyle, setStrokeStyle] = useState<StrokeStyle | undefined>(undefined);

  const {
    mutateAsync: upsertShotStyle,
    isPending,
    data: result,
    error,
  } = upsertAiEditShotStyle.useMutation({
    onSuccess: () => {
      toast.success("Shot style created successfully");
    },
    onError: () => {
      toast.error("Failed to create shot style");
    },
  });

  const { mutateAsync: addShotStyle } = addShotStyleToVideoStyle.useMutation({
    onSuccess: () => {
      toast.success("Shot style added to video style successfully");
    },
    onError: () => {
      toast.error("Failed to add shot style to video style");
    },
  });

  const onSubmit = useCallback(async () => {
    const hasInvalidAiTextLayerRefs =
      hasInvalidAiTitleLayerReferences(foregroundOverlays) ||
      hasInvalidAiTitleLayerReferences(backgroundOverlays);

    if (hasInvalidAiTextLayerRefs) {
      toast.error("Each AI text slot must include at least one valid layer index");
      return;
    }

    const captions = createCaptions(
      captionLayout,
      captionOverrideTemplateId || undefined,
      captionColorOverrides
    );
    const convertedForegroundOverlays = convertOverlaysToApiFormat(foregroundOverlays);
    const convertedBackgroundOverlays = convertOverlaysToApiFormat(backgroundOverlays);
    const soundEffects = soundEffectConfigs.map((config) =>
      createSoundEffect(config.assetId, config.volume, config.mode)
    );

    const payload = {
      id: "",
      shotTemplate: {
        id: "",
        displayName,
        metadata: {
          shotType,
          category: DisplayCategoryStringToNum[displayCategory] ?? 0,
          supportedVideoTypes,
          thumbnailUrl: thumbnailUrl || undefined,
          ios: { inDev: true },
          desktop: { inDev: true },
          android: { inDev: true },
        },
        captions,
        foregroundOverlays: convertedForegroundOverlays,
        backgroundOverlays: convertedBackgroundOverlays,
        soundEffects,
        ...(strokeStyle ? { strokeStyle } : {}),
      },
    };

    const upsertShotStyleResponse = await upsertShotStyle({
      params: payload,
    });

    await addShotStyle({
      params: {
        videoStyleId,
        shotStyleId: upsertShotStyleResponse.id,
        isDefaultShotStyle,
      },
    });
  }, [
    displayName,
    shotType,
    displayCategory,
    supportedVideoTypes,
    captionLayout,
    captionOverrideTemplateId,
    captionColorOverrides,
    foregroundOverlays,
    backgroundOverlays,
    soundEffectConfigs,
    thumbnailUrl,
    isDefaultShotStyle,
    strokeStyle,
    videoStyleId,
    upsertShotStyle,
    addShotStyle,
  ]);

  const formJsonData = useMemo(
    () => ({
      displayName,
      metadata: {
        shotType,
        supportedVideoTypes,
        thumbnailUrl,
        ios: { inDev: true },
        desktop: { inDev: true },
        android: { inDev: true },
      },
      captions: {
        layout: captionLayout,
        overrideTemplateId: captionOverrideTemplateId,
        colorOverrides: captionColorOverrides,
      },
      ...(strokeStyle ? { strokeStyle } : {}),
      foregroundOverlays,
      backgroundOverlays,
      soundEffects: soundEffectConfigs,
    }),
    [
      displayName,
      shotType,
      supportedVideoTypes,
      thumbnailUrl,
      captionLayout,
      captionOverrideTemplateId,
      captionColorOverrides,
      strokeStyle,
      foregroundOverlays,
      backgroundOverlays,
      soundEffectConfigs,
    ]
  );

  if (!canEdit || !canEditVideoStyle) {
    return (
      <div className="flex flex-col gap-4 h-full">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => {
              navigate({ to: "/ai-edit-tool" });
            }}
            size="sm"
          >
            {"< Back"}
          </Button>
        </div>
        <PermissionBanner requiredPermission="ai-edit-v2-tool:write" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => {
            navigate({ to: "/ai-edit-tool" });
          }}
          size="sm"
        >
          {"< Back"}
        </Button>
        <Button
          variant="outline"
          onClick={handleNavigateToPrevious}
          disabled={!hasAnyShots}
          size="sm"
          className="px-2"
          title="View last shot style"
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        <Button
          variant="outline"
          onClick={handleNavigateToNext}
          disabled={!hasAnyShots}
          size="sm"
          className="px-2"
          title="View first shot style"
        >
          <Icon name="chevron-right" size={16} />
        </Button>
        <ShotStyleModeIndicator mode="create" />
        <Button
          onClick={onSubmit}
          disabled={isPending || !videoStyleId || !displayName}
          size="sm"
          className="ml-auto"
        >
          {isPending ? "Submitting..." : "Submit"}
        </Button>
      </div>

      <div className="text-xs text-muted-foreground font-mono">
        Video Style: {videoStyle?.displayName || videoStyleId} ({videoStyleId})
      </div>

      {result && (
        <div className="flex flex-col gap-4 border border-border rounded p-4 bg-muted/30">
          <h2 className="text-lg font-semibold font-mono text-green-600">Success!</h2>
          <p className="text-sm font-mono">Shot style ID: {result.id}</p>
          <pre className="text-xs font-mono bg-muted p-4 rounded overflow-auto max-h-48">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}

      {error && (
        <div className="flex flex-col gap-4 border border-destructive rounded p-4">
          <h2 className="text-lg font-semibold font-mono text-destructive">Error!</h2>
          <p className="text-sm font-mono">{error.message}</p>
          <Button onClick={() => window.location.reload()} size="sm">
            Try again
          </Button>
        </div>
      )}

      {!result && !error && (
        <div className="flex-1 min-h-0">
          <ShotStyleForm
            videoStyleId={videoStyleId}
            basicInfo={{
              displayName,
              shotType,
              displayCategory,
              supportedVideoTypes,
              isDefaultShotStyle,
            }}
            onBasicInfoChange={(info) => {
              setDisplayName(info.displayName);
              setShotType(info.shotType);
              setDisplayCategory(info.displayCategory);
              setSupportedVideoTypes(info.supportedVideoTypes);
              setIsDefaultShotStyle(info.isDefaultShotStyle);
            }}
            captions={{
              captionLayout,
              captionOverrideTemplateId,
              captionColorOverrides,
            }}
            onCaptionsChange={(captions) => {
              setCaptionLayout(captions.captionLayout);
              setCaptionOverrideTemplateId(captions.captionOverrideTemplateId);
              setCaptionColorOverrides(captions.captionColorOverrides || []);
            }}
            strokeStyle={strokeStyle}
            onStrokeStyleChange={setStrokeStyle}
            foregroundOverlays={foregroundOverlays}
            onForegroundOverlaysChange={setForegroundOverlays}
            backgroundOverlays={backgroundOverlays}
            onBackgroundOverlaysChange={setBackgroundOverlays}
            soundEffects={soundEffectConfigs}
            onSoundEffectsChange={setSoundEffectConfigs}
            jsonData={formJsonData}
            thumbnailUrl={thumbnailUrl}
            onThumbnailUrlChange={setThumbnailUrl}
          />
        </div>
      )}
    </div>
  );
}
